import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import { TEXT_COLOR } from '../../theme';

const CELL_SX = { fontSize: '12.5px', color: 'text.secondary' };

// Resultado de una corrida, en una sola celda: el nivel cuando lo hay, y el
// motivo por el que no lo hay cuando la corrida no llegó a uno.
function Outcome({ tr, run }) {
    if (run.status === 'P') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CircularProgress size={13} />
                <Typography sx={CELL_SX}>{tr('ai_run_pending')}</Typography>
            </Box>
        );
    }
    if (run.status === 'E') {
        return (
            <Tooltip title={run.error || ''}>
                <Chip label={tr('ai_run_error')} size="small"
                      sx={{ height: 22, fontSize: 11.5, bgcolor: '#FDECE7', color: '#D04A1F' }}/>
            </Tooltip>
        );
    }
    if (run.is_tied) {
        return (
            <Tooltip title={tr('ai_tied')}>
                <Chip label={tr('ai_run_tied')} size="small"
                      sx={{ height: 22, fontSize: 11.5, bgcolor: '#FDF3E0', color: '#9A6400' }}/>
            </Tooltip>
        );
    }
    return (
        <Chip label={`${run.level}${run.sublevel}`} color="primary" size="small"
              sx={{ height: 22, fontWeight: 700 }}/>
    );
}

/**
 * Corridas de clasificación de una misma encuesta, una por fila.
 *
 * Existe para comparar specs: el mismo texto clasificado con dos modelos puede
 * dar niveles distintos, y quien etiqueta necesita ver ambos antes de decidir el
 * nivel que carga a mano. Elegir una fila muestra arriba su nivel y su
 * fundamento, y deja su spec cargado para volver a correrlo.
 */
export default function AIClassificationRuns({ tr, runs, shownRunId, onSelectRun }) {
    if (runs.length === 0) return null;

    return (
        <Box sx={{ mt: 3 }} data-testid="ai-classification-runs">
            <Typography sx={{
                fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.07em', color: TEXT_COLOR, mb: 1,
            }}>
                {`${tr('ai_runs')} (${runs.length})`}
            </Typography>

            <Box sx={{ border: '1px solid #E1E4E8', borderRadius: '10px', overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                    <Box component="thead">
                        <Box component="tr" sx={{ bgcolor: '#F4F5F7' }}>
                            {['ai_run_spec', 'ai_run_result', 'ai_run_cost', 'ai_run_date'].map((key) => (
                                <Box component="th" key={key} sx={{
                                    textAlign: 'left', px: 1.5, py: 1,
                                    fontSize: '11px', fontWeight: 600, color: TEXT_COLOR,
                                    textTransform: 'uppercase', letterSpacing: '0.05em',
                                }}>
                                    {tr(key)}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {runs.map((run) => {
                            const active = run.id === shownRunId;
                            return (
                            <Box
                                component="tr"
                                key={run.id}
                                role="button"
                                tabIndex={0}
                                aria-pressed={active}
                                onClick={() => onSelectRun(run)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onSelectRun(run);
                                    }
                                }}
                                sx={{
                                    borderTop: '1px solid #E1E4E8',
                                    cursor: 'pointer',
                                    bgcolor: active ? '#EEF2FB' : 'transparent',
                                    '&:hover': { bgcolor: active ? '#EEF2FB' : '#F4F5F7' },
                                }}
                            >
                                <Box component="td" sx={{ px: 1.5, py: 1 }}>
                                    <Typography sx={{
                                        fontFamily: 'ui-monospace, monospace',
                                        fontSize: '12px',
                                        color: active ? 'primary.main' : 'text.secondary',
                                        fontWeight: active ? 700 : 400,
                                    }}>
                                        {run.spec_name}
                                    </Typography>
                                </Box>
                                <Box component="td" sx={{ px: 1.5, py: 1 }}>
                                    <Outcome tr={tr} run={run} />
                                </Box>
                                <Box component="td" sx={{ px: 1.5, py: 1, ...CELL_SX, whiteSpace: 'nowrap' }}>
                                    {run.status === 'O'
                                        ? `${run.input_tokens + run.output_tokens} ${tr('ai_tokens')} · ${Number(run.duration_seconds).toFixed(1)}s`
                                        : '—'}
                                </Box>
                                <Box component="td" sx={{ px: 1.5, py: 1, ...CELL_SX, whiteSpace: 'nowrap' }}>
                                    {dayjs(run.created_date).format('D/M/YY HH:mm')}
                                </Box>
                            </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
